import { Button, ConfirmDialog, FormField, Modal, SearchInput, Select, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  productionRequestStatusSchema,
  type ProductionRequestListItem,
} from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  acceptRequest,
  createRequest,
  deliverRequest,
  fetchRequests,
  productionKeys,
  rejectRequest,
} from '../api';
import { ArticlePicker } from '../components/Pickers';
import { RequestKanban } from '../components/RequestKanban';
import { PageHeader } from '../../../components/PageHeader';
import { usePermission } from '../../../auth/auth-store';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useListState } from '../../../hooks/useListState';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * Spec §5.9: production orders the goods it needs, the warehouse sees the
 * request, accepts it (which raises a move task) and delivers it — the delivery
 * being the actual stock move onto the production floor bin.
 */
export function ProductionRequestsPage(): ReactNode {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();
  const canCreate = usePermission('production.requests.create');
  const canManage = usePermission('production.requests.manage');

  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc', defaultPageSize: 200 });
  const [creating, setCreating] = useState(false);
  const [rejecting, setRejecting] = useState<ProductionRequestListItem | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);

  const query = useQuery({ queryKey: productionKeys.requests(state), queryFn: () => fetchRequests(state) });
  useLiveQuery(['production', 'stock'], ['production', 'requests']);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: productionKeys.all });
  };

  const mutate = useMutation({
    mutationFn: async ({
      request,
      action,
    }: {
      request: ProductionRequestListItem;
      action: 'accept' | 'deliver';
    }) => (action === 'accept' ? acceptRequest(request.id) : deliverRequest(request.id)),
    onMutate: ({ request }) => {
      setBusyRequestId(request.id);
    },
    onSuccess: (_result, { action }) => {
      toastSuccess(action === 'accept' ? t('requests.toasts.accepted') : t('requests.toasts.delivered'));
      refresh();
    },
    onError: (err) => {
      toastError(err);
    },
    onSettled: () => {
      setBusyRequestId(null);
    },
  });

  return (
    <>
      <PageHeader
        title={t('requests.title')}
        subtitle={t('requests.subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('requests.new')}
            </Button>
          ) : null
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          variant="outline"
          className="w-full sm:w-72"
          value={state.q ?? ''}
          onChange={(value) => {
            setState({ ...state, q: value || undefined, page: 1 });
          }}
          onClear={() => {
            setState({ ...state, q: undefined, page: 1 });
          }}
          placeholder={t('requests.searchPlaceholder')}
          aria-label={t('requests.searchPlaceholder')}
        />
        <Select
          aria-label={t('requests.status.requested')}
          className="w-48"
          options={[
            { value: '', label: t('orders.filters.allStatuses') },
            ...productionRequestStatusSchema.options.map((status) => ({
              value: status,
              label: t(`requests.status.${status}`),
            })),
          ]}
          value={String(state.filters?.status ?? '')}
          onChange={(event) => {
            setFilter('status', event.target.value || undefined);
          }}
        />
      </div>

      <RequestKanban
        requests={query.data?.items ?? []}
        canManage={canManage}
        busyRequestId={busyRequestId}
        emptyLabel={t('requests.empty')}
        onAccept={(request) => {
          mutate.mutate({ request, action: 'accept' });
        }}
        onDeliver={(request) => {
          mutate.mutate({ request, action: 'deliver' });
        }}
        onReject={setRejecting}
      />

      {creating ? (
        <CreateRequestModal
          onClose={() => {
            setCreating(false);
          }}
          onDone={refresh}
        />
      ) : null}

      {rejecting ? (
        <ConfirmDialog
          title={t('requests.confirm.rejectTitle')}
          message={t('requests.confirm.rejectMessage')}
          variant="danger"
          confirmLabel={t('requests.confirm.rejectConfirm')}
          cancelLabel={t('requests.actions.cancel')}
          onClose={() => {
            setRejecting(null);
          }}
          onConfirm={async () => {
            try {
              await rejectRequest(rejecting.id);
              toastSuccess(t('requests.toasts.rejected'));
              refresh();
            } catch (err) {
              toastError(err);
            } finally {
              setRejecting(null);
            }
          }}
        />
      ) : null}
    </>
  );
}

function CreateRequestModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }): ReactNode {
  const { t } = useTranslation('production');
  const [articleId, setArticleId] = useState('');
  const [qtyBase, setQtyBase] = useState('');
  const [neededAt, setNeededAt] = useState('');
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createRequest({
        articleId,
        qtyBase: qtyBase.trim(),
        ...(neededAt ? { neededAt: new Date(neededAt).toISOString() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      toastSuccess(t('requests.toasts.created'));
      onDone();
      onClose();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('requests.new')} width={480}>
      <div className="flex flex-col gap-4">
        <ArticlePicker value={articleId} onChange={setArticleId} label={t('requests.fields.article')} />
        <FormField
          label={t('requests.fields.qty')}
          inputMode="decimal"
          value={qtyBase}
          onChange={(event) => {
            setQtyBase(event.target.value);
          }}
        />
        <FormField
          label={t('requests.fields.neededAt')}
          type="datetime-local"
          value={neededAt}
          onChange={(event) => {
            setNeededAt(event.target.value);
          }}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">{t('requests.fields.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('requests.actions.cancel')}
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={!articleId || !qtyBase.trim()}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {t('requests.actions.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
